import React, { useState } from "react";
import { Play, Calendar, User, Tag, X } from "lucide-react";
import { toEmbedUrl, youtubeThumb } from "@/lib/video";
import VideoPlayer from "@/components/site/VideoPlayer";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  } catch { return iso; }
}

function thumbnailFor(c) {
  if (c.thumbnail_url) return c.thumbnail_url;
  if (c.video_type === "youtube") return youtubeThumb(c.video_url);
  return "https://images.pexels.com/photos/34328510/pexels-photo-34328510.jpeg";
}

export default function CultesSection({ cultes = [] }) {
  const [selected, setSelected] = useState(null);

  const embed = selected ? toEmbedUrl(selected.video_type, selected.video_type === "upload"
    ? `${process.env.REACT_APP_BACKEND_URL}${selected.video_url}` : selected.video_url) : "";

  return (
    <section id="cultes" className="py-24 lg:py-32 bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-gold-dark text-xs tracking-[0.3em] uppercase mb-4">
              <Play className="w-3.5 h-3.5" /> Médiathèque
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-deepblue-900 leading-tight">
              Revoir les cultes passés
            </h2>
            <p className="mt-4 text-slate-600 text-lg">
              Revisitez les messages, prières et enseignements de nos rencontres précédentes.
            </p>
          </div>
          <div className="text-sm text-slate-500">
            <span className="font-semibold text-deepblue-900">{cultes.length}</span> {cultes.length > 1 ? "cultes disponibles" : "culte disponible"}
          </div>
        </div>

        {cultes.length === 0 ? (
          <div className="text-center py-24 text-slate-500 border-2 border-dashed border-slate-200 rounded-2xl">
            Aucun culte enregistré pour le moment.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="cultes-grid">
            {cultes.map((c) => (
              <button
                key={c.id}
                onClick={() => setSelected(c)}
                data-testid={`culte-card-${c.id}`}
                className="group text-left bg-white rounded-2xl overflow-hidden border border-slate-100 card-lift"
              >
                <div className="relative aspect-video overflow-hidden bg-slate-100">
                  <img
                    src={thumbnailFor(c)}
                    alt={c.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => { e.currentTarget.src = "https://images.pexels.com/photos/34328510/pexels-photo-34328510.jpeg"; }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-deepblue-900/80 via-transparent to-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-16 h-16 rounded-full bg-gold flex items-center justify-center shadow-2xl">
                      <Play className="w-7 h-7 text-white fill-white ml-1" />
                    </div>
                  </div>
                  {c.duration && (
                    <div className="absolute bottom-3 right-3 px-2 py-1 text-xs bg-black/70 text-white rounded">
                      {c.duration}
                    </div>
                  )}
                  {c.theme && (
                    <div className="absolute top-3 left-3 px-2.5 py-1 text-[11px] uppercase tracking-wider bg-gold text-white rounded-full font-semibold">
                      {c.theme}
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="font-serif text-xl text-deepblue-900 font-semibold leading-snug group-hover:text-gold-dark transition-colors">
                    {c.title}
                  </h3>
                  <div className="mt-4 flex items-center gap-4 text-sm text-slate-500">
                    <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{formatDate(c.date)}</span>
                    <span className="inline-flex items-center gap-1.5"><User className="w-3.5 h-3.5" />{c.preacher}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-5xl bg-deepblue-900 text-white border-white/10 p-0">
          <DialogTitle className="sr-only">{selected?.title}</DialogTitle>
          <button
            onClick={() => setSelected(null)}
            className="absolute right-4 top-4 z-20 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
            data-testid="close-video-modal"
          >
            <X className="w-5 h-5" />
          </button>
          {selected && (
            <div>
              <div className="video-16-9 bg-black">
                <VideoPlayer type={selected.video_type} url={embed} autoPlay title={selected.title} />
              </div>
              <div className="p-6 sm:p-8">
                {selected.theme && (
                  <div className="inline-flex items-center gap-1.5 text-gold text-xs tracking-[0.25em] uppercase mb-3">
                    <Tag className="w-3 h-3" /> {selected.theme}
                  </div>
                )}
                <h3 className="font-serif text-2xl sm:text-3xl">{selected.title}</h3>
                <div className="mt-3 flex items-center gap-5 text-sm text-white/70">
                  <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4" />{formatDate(selected.date)}</span>
                  <span className="inline-flex items-center gap-1.5"><User className="w-4 h-4" />{selected.preacher}</span>
                </div>
                {selected.description && (
                  <p className="mt-5 text-white/75 leading-relaxed">{selected.description}</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
